import { IAuthService } from './types';
import { fetchAuthSession } from 'aws-amplify/auth';

export class AuthV4Service implements IAuthService {
  static #instance: IAuthService;
  #pendingGetToken?: Promise<string | undefined>;

  private constructor() {
    // private constructor to allow singleton pattern
  }

  public static getInstance(): IAuthService {
    if (!AuthV4Service.#instance) {
      AuthV4Service.#instance = new AuthV4Service();
    }

    return AuthV4Service.#instance;
  }

  public getToken(forceRefresh = false): Promise<string | undefined> {
    // Several independent call sites check for a session on mount (AuthProvider's own two
    // effects, AppRoutes', useTeamsSignIn's, ...), often within the same tick. Without sharing
    // the in-flight request, each fires its own fetchAuthSession() — and since that can itself
    // trigger a Cognito token refresh, overlapping calls mean overlapping refresh attempts
    // against the same (possibly already-stale) refresh token. That's what produced spurious
    // "refresh token expired" failures landing right after an otherwise-successful sign-in:
    // a redundant concurrent call, not the sign-in itself, was the one failing.
    //
    // forceRefresh deliberately bypasses that same cache: a caller checking the session right
    // after a sign-in (or after writing fresh Cognito cookies) needs a call that's guaranteed to
    // start after that event, not a pre-existing pending promise from an unrelated mount-time
    // check that would resolve to the pre-sign-in (no session) state.
    if (forceRefresh || !this.#pendingGetToken) {
      this.#pendingGetToken = fetchAuthSession({ forceRefresh })
        .then((session) => session.tokens?.accessToken?.toString())
        .finally(() => {
          this.#pendingGetToken = undefined;
        });
    }

    return this.#pendingGetToken;
  }
}
